import React from 'react';
import { Filter, RotateCcw } from 'lucide-react';
import { CustomDropdown } from '../../../components/Common/CustomDropdown';

export interface FilterState {
  name: string;
  phone: string;
  zone: string;
  city: string;
  depo: string;
  route: string;
}

interface DistributorFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onReset: () => void;
  zoneOptions: string[];
  cityOptions: string[];
  depoOptions: string[];
  routeOptions: string[];
}

export const DistributorFilters: React.FC<DistributorFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
  zoneOptions,
  cityOptions,
  depoOptions,
  routeOptions,
}) => {
  const handleChange = (field: keyof FilterState, value: string) => {
    onFilterChange({
      ...filters,
      [field]: value,
    });
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-[#ECECF2] shadow-sm mb-6 select-none">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        {/* Name input */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-slate-700">Name</label>
          <input
            type="text"
            placeholder="Search by name"
            value={filters.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full bg-slate-50/50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] focus:ring-1 focus:ring-[#C1272D] transition-all"
          />
        </div>

        {/* Phone input */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-bold text-slate-700">Phone</label>
          <input
            type="text"
            placeholder="Search by phone"
            value={filters.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full bg-slate-50/50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] focus:ring-1 focus:ring-[#C1272D] transition-all"
          />
        </div>

        {/* Zone dropdown */}
        <CustomDropdown
          label="Zone"
          placeholder="Select zone"
          options={['Select zone', ...zoneOptions]}
          value={filters.zone || 'Select zone'}
          onChange={(val: string) => handleChange('zone', val === 'Select zone' ? '' : val)}
        />

        {/* City dropdown */}
        <CustomDropdown
          label="City"
          placeholder="Select city"
          options={['Select city', ...cityOptions]}
          value={filters.city || 'Select city'}
          onChange={(val: string) => handleChange('city', val === 'Select city' ? '' : val)}
        />

        {/* Depo dropdown */}
        <CustomDropdown
          label="Depo"
          placeholder="Select depo"
          options={['Select depo', ...depoOptions]}
          value={filters.depo || 'Select depo'}
          onChange={(val: string) => handleChange('depo', val === 'Select depo' ? '' : val)}
        />

        {/* Route dropdown */}
        <CustomDropdown
          label="Route"
          placeholder="Select route"
          options={['Select route', ...routeOptions]}
          value={filters.route || 'Select route'}
          onChange={(val: string) => handleChange('route', val === 'Select route' ? '' : val)}
        />
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-end gap-3 mt-4 pt-3 border-t border-slate-100">
        <button
          onClick={onReset}
          className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
        >
          <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
          Reset
        </button>

        <button
          className="px-5 py-2 bg-[#C1272D] hover:bg-[#A81E23] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-md shadow-[#C1272D]/20 active:scale-95"
        >
          <Filter className="w-3.5 h-3.5" />
          Filter
        </button>
      </div>
    </div>
  );
};
